/**
 * Minecraft skins as COMMUNITY DESIGNS — the parts that are safe anywhere (client and server): the
 * stored state format, the 64x64 UV layout, the flat front/back views the gallery draws, and a cheap
 * structural check the editor can run before anything is saved.
 *
 * The real safety check (decode on the server, re-encode from raw pixels) is in
 * `types/skin.server.ts` + `skinPng.ts`, which need Node's zlib.
 *
 * STATE FORMAT: `<model>:<base64 PNG>`, model `classic` | `slim`. The canonical form (what is stored and
 * served) has no `data:` prefix; the form read from a saved build (`saved_elements.metadata.textureBase64`
 * is a `canvas.toDataURL()` data URL) may carry one.
 */

export type SkinModel = 'classic' | 'slim';

export const SKIN_SIZE = 64;
/** Decoded PNG bytes a PUBLISH may read (the saved build — a browser's PNG encoder output). */
export const SKIN_INPUT_MAX_BYTES = 64 * 1024;
/**
 * Longest canonical state. A canonical skin is re-encoded by us with filter 0 + deflate level 9; for
 * incompressible noise that is 16 448 raw bytes stored → ~16.5 KB PNG → ~22 030 base64 chars + prefix.
 * The self-test asserts a random-noise skin stays under this. Mirrored by the database CHECK
 * (migration 20260919031942).
 */
export const SKIN_STATE_MAX = 24576;

const STATE_RE = /^(classic|slim):(?:data:image\/png;base64,)?([A-Za-z0-9+/]+={0,2})$/;

export function parseSkinState(state: string): { model: SkinModel; base64: string } | null {
  // Bounded before the regex runs: an input can be as long as a saved row allows.
  if (typeof state !== 'string' || state.length === 0 || state.length > Math.ceil(SKIN_INPUT_MAX_BYTES / 3) * 4 + 40) return null;
  const m = STATE_RE.exec(state);
  if (!m || m[2].length % 4 !== 0) return null;
  return { model: m[1] as SkinModel, base64: m[2] };
}

export function skinDataUrl(base64: string): string {
  return `data:image/png;base64,${base64}`;
}

// ── UV layout ──────────────────────────────────────────────────────────────────────────────────────
// Every part is a box (w × h × d) whose six faces are unwrapped from its (u, v) corner:
//   top    (u+d,     v,   w, d)   bottom (u+d+w,   v,   w, d)
//   right  (u,       v+d, d, h)   front  (u+d,     v+d, w, h)
//   left   (u+d+w,   v+d, d, h)   back   (u+2d+w,  v+d, w, h)
// Slim ("Alex") arms are 3 pixels wide instead of 4.

interface Box { u: number; v: number; w: number; h: number; d: number }
type PartName = 'head' | 'body' | 'rightArm' | 'leftArm' | 'rightLeg' | 'leftLeg';
interface Part { base: Box; overlay: Box }

export function skinParts(model: SkinModel): Record<PartName, Part> {
  const aw = model === 'slim' ? 3 : 4;
  return {
    head: { base: { u: 0, v: 0, w: 8, h: 8, d: 8 }, overlay: { u: 32, v: 0, w: 8, h: 8, d: 8 } },
    body: { base: { u: 16, v: 16, w: 8, h: 12, d: 4 }, overlay: { u: 16, v: 32, w: 8, h: 12, d: 4 } },
    rightArm: { base: { u: 40, v: 16, w: aw, h: 12, d: 4 }, overlay: { u: 40, v: 32, w: aw, h: 12, d: 4 } },
    leftArm: { base: { u: 32, v: 48, w: aw, h: 12, d: 4 }, overlay: { u: 48, v: 48, w: aw, h: 12, d: 4 } },
    rightLeg: { base: { u: 0, v: 16, w: 4, h: 12, d: 4 }, overlay: { u: 0, v: 32, w: 4, h: 12, d: 4 } },
    leftLeg: { base: { u: 16, v: 48, w: 4, h: 12, d: 4 }, overlay: { u: 0, v: 48, w: 4, h: 12, d: 4 } },
  };
}

/**
 * Which of the 64x64 texture's pixels Minecraft ever draws for this model (1 = used). Everything else is
 * never visible in game NOR in the gallery preview a moderator approves — so the canonical form clears
 * it, and it cannot carry an image past review.
 */
export function usedMask(model: SkinModel): Uint8Array {
  const mask = new Uint8Array(SKIN_SIZE * SKIN_SIZE);
  const fill = (x: number, y: number, w: number, h: number) => {
    for (let j = y; j < y + h; j++) for (let i = x; i < x + w; i++) mask[j * SKIN_SIZE + i] = 1;
  };
  for (const part of Object.values(skinParts(model))) {
    for (const b of [part.base, part.overlay]) {
      fill(b.u + b.d, b.v, 2 * b.w, b.d);            // top + bottom
      fill(b.u, b.v + b.d, 2 * b.d + 2 * b.w, b.h);   // right, front, left, back
    }
  }
  return mask;
}

/** A flat view: 16 x 32 RGBA, the character seen straight on, overlay composited over base. */
export const VIEW_W = 16;
export const VIEW_H = 32;

/**
 * Front or back view of a 64x64 RGBA skin. `face` picks the box face (front: u+d; back: u+2d+w), and on
 * the back view the character's LEFT side is on the viewer's left, so the limbs swap places.
 */
export function renderSkinView(rgba: Uint8Array | Uint8ClampedArray, model: SkinModel, side: 'front' | 'back'): Uint8ClampedArray {
  const out = new Uint8ClampedArray(VIEW_W * VIEW_H * 4);
  const parts = skinParts(model);
  const aw = model === 'slim' ? 3 : 4;
  // Destination x of each part on the front view; the back view mirrors the layout.
  const frontX: Record<PartName, number> = {
    head: 4, body: 4, rightArm: 4 - aw, leftArm: 12, rightLeg: 4, leftLeg: 8,
  };
  const destY: Record<PartName, number> = { head: 0, body: 8, rightArm: 8, leftArm: 8, rightLeg: 20, leftLeg: 20 };
  const draw = (b: Box, dx: number, dy: number) => {
    const sx0 = side === 'front' ? b.u + b.d : b.u + 2 * b.d + b.w;
    const sy0 = b.v + b.d;
    for (let y = 0; y < b.h; y++) {
      for (let x = 0; x < b.w; x++) {
        const s = ((sy0 + y) * SKIN_SIZE + sx0 + x) * 4;
        const d = ((dy + y) * VIEW_W + dx + x) * 4;
        const a = rgba[s + 3] / 255;
        if (a === 0) continue;
        const da = out[d + 3] / 255;
        const oa = a + da * (1 - a);
        for (let c = 0; c < 3; c++) out[d + c] = Math.round((rgba[s + c] * a + out[d + c] * da * (1 - a)) / oa);
        out[d + 3] = Math.round(oa * 255);
      }
    }
  };
  for (const layer of ['base', 'overlay'] as const) {
    for (const name of Object.keys(parts) as PartName[]) {
      const box = parts[name][layer];
      const fx = frontX[name];
      // Mirror across the view's vertical center line: a part at x..x+w lands at 16-x-w.
      const dx = side === 'front' ? fx : VIEW_W - fx - box.w;
      draw(box, dx, destY[name]);
    }
  }
  return out;
}

// ── Cheap structural check (no inflate) ─────────────────────────────────────────────────────────────

function base64Bytes(base64: string, max: number): Uint8Array | null {
  try {
    const bin = atob(base64.slice(0, Math.ceil(max / 3) * 4));
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}

/** Decoded byte length of a padded base64 string. */
export function base64ByteLength(base64: string): number {
  return (base64.length / 4) * 3 - (base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0);
}

/**
 * The part of the safety check that needs no decompression — shape, size, PNG header — for the editor's
 * instant feedback (the CLIENT adapter's `publishProblem`). ADVISORY: the server decodes every pixel.
 */
export function skinShapeProblem(state: string): string | null {
  if (state === '') return 'Only skins painted or uploaded in the Skin Editor can be published — not a player’s skin saved from a username lookup.';
  const parsed = parseSkinState(state);
  if (!parsed) return 'That skin could not be read.';
  if (base64ByteLength(parsed.base64) > SKIN_INPUT_MAX_BYTES) return 'That skin file is too large.';
  const head = base64Bytes(parsed.base64, 33);
  const SIG = [137, 80, 78, 71, 13, 10, 26, 10];
  if (!head || head.length < 33 || SIG.some((b, i) => head[i] !== b)) return 'That skin is not a PNG image.';
  const width = ((head[16] << 24) | (head[17] << 16) | (head[18] << 8) | head[19]) >>> 0;
  const height = ((head[20] << 24) | (head[21] << 16) | (head[22] << 8) | head[23]) >>> 0;
  if (width === 64 && height === 32) return LEGACY_SKIN_PROBLEM;
  if (width !== SKIN_SIZE || height !== SKIN_SIZE) return 'A skin must be 64 × 64 pixels.';
  return null;
}

export const LEGACY_SKIN_PROBLEM = 'Old 64 × 32 skins can’t be published as they are — open it in the Skin Editor and save it again, which converts it to 64 × 64.';
